import * as React from "react"
import { Link } from "@inertiajs/react"
import { Flag, Search } from "lucide-react"
import { PublicLogo } from "@/components/PublicLogo"
import { ProblemReportSheet } from "@/components/ProblemReportSheet"
import { AnalyticsConsentBanner, AnalyticsPreferencesButton } from "@/components/AnalyticsConsent"

type Locale = "en" | "de"

const localeLabels: Record<Locale, string> = {
  en: "EN",
  de: "DE",
}

const footerCopy = {
  en: {
    searchPlaceholder: "Search places, ferries, trails...",
    searchLabel: "Search",
    report: "Help keep local details current",
    imprint: "Imprint",
    privacy: "Privacy",
    dataLicence: "Data licence",
    note: "© 2026 Xplore Croatia - a field guide, not a booking site.",
  },
  de: {
    searchPlaceholder: "Orte, Fähren und Wege suchen...",
    searchLabel: "Suche",
    report: "Hilf, Ortsangaben aktuell zu halten",
    imprint: "Impressum",
    privacy: "Datenschutz",
    dataLicence: "Datenlizenz",
    note: "© 2026 Xplore Croatia - ein Reiseführer, keine Buchungsseite.",
  },
} satisfies Record<Locale, Record<string, string>>

export function PublicShell({
  locale,
  localePaths,
  hideFooter = false,
  children,
}: {
  locale: Locale
  localePaths?: Partial<Record<Locale, string>>
  hideFooter?: boolean
  children: React.ReactNode
}) {
  const homeHref = `/${locale}`
  const alternateLocale = locale === "en" ? "de" : "en"
  const pathFor = (candidate: Locale) => localePaths?.[candidate] ?? `/${candidate}`
  const footer = footerCopy[locale]

  return (
    <div className="public-frame">
      <header className="public-header">
        <div className="public-container flex h-[3.875rem] items-center gap-3">
          <PublicLogo href={homeHref} compact />
          <div className="ml-auto flex items-center gap-2 md:gap-3">
            <form action={`/${locale}/search`} method="get" className="hidden w-[21.25rem] items-center gap-2 rounded-full border border-hairline bg-surface px-3.5 py-2 md:flex">
              <Search className="h-[0.95rem] w-[0.95rem] shrink-0 text-ink-faint" aria-hidden />
              <input name="q" minLength={2} aria-label={footer.searchLabel} placeholder={footer.searchPlaceholder} className="min-w-0 flex-1 bg-transparent text-[0.84rem] outline-none placeholder:text-ink-muted" />
            </form>
            <Link
              href={`/${locale}/search`}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-hairline bg-surface text-ink-secondary hover:bg-surface-subtle md:hidden"
              aria-label={footer.searchLabel}
            >
              <Search className="h-4 w-4" aria-hidden />
            </Link>
            <AnalyticsPreferencesButton locale={locale} compact />
            <nav
              className="flex overflow-hidden rounded-full border border-hairline bg-surface text-[0.7rem] font-semibold"
              aria-label="Language"
            >
              {(["en", "de"] as Locale[]).map((candidate) => (
                <Link
                  key={candidate}
                  href={pathFor(candidate)}
                  className={
                    candidate === locale
                      ? "bg-accent-hover px-2.5 py-1.5 text-surface no-underline md:px-3"
                      : "px-2.5 py-1.5 text-ink-muted no-underline hover:bg-surface-subtle hover:text-ink-display md:px-3"
                  }
                  aria-current={candidate === locale ? "page" : undefined}
                >
                  {localeLabels[candidate]}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </header>

      {children}
      <ProblemReportSheet locale={locale} />
      <AnalyticsConsentBanner locale={locale} />

      {!hideFooter && <footer className="border-t border-hairline-strong">
        <div className="public-container flex flex-col gap-3 py-6 text-sm text-ink-muted md:flex-row md:items-center md:gap-5">
          <span className="inline-flex items-center gap-2 self-start text-sm font-semibold text-ink-muted">
            <Flag className="h-3.5 w-3.5" aria-hidden />
            {footer.report}
          </span>
          <Link href={pathFor(alternateLocale)} className="text-ink-muted no-underline hover:text-ink-display">
            {alternateLocale === "en" ? "English" : "Deutsch"}
          </Link>
          <Link href={homeHref} className="text-ink-muted no-underline hover:text-ink-display">
            {footer.imprint}
          </Link>
          <Link href={homeHref} className="text-ink-muted no-underline hover:text-ink-display">
            {footer.privacy}
          </Link>
          <Link href={`/${locale}/data-licence`} className="text-ink-muted no-underline hover:text-ink-display">
            {footer.dataLicence}
          </Link>
          <AnalyticsPreferencesButton locale={locale} />
          <span className="text-ink-muted md:ml-auto">{footer.note}</span>
        </div>
      </footer>}
    </div>
  )
}
